import { CircularProgress } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { AiMappingController } from "../useAiMappingController";
import AiMappingDrawForm from "./AiMappingDrawForm";
import AiMappingUpsell from "./AiMappingUpsell";
import AiMappingGlobalMeasurements, { ManualMeasurement } from "./AiMappingGlobalMeasurements";
import AiMappingRunAreaEditor from "./AiMappingRunAreaEditor";

export default function AiMappingSidebar({
  controller,
  combinedMeasuredAreaSqft = 0,
  manualPerimeterFeet = 0,
  manualMeasurements = [],
}: {
  controller: AiMappingController;
  combinedMeasuredAreaSqft?: number;
  manualPerimeterFeet?: number;
  manualMeasurements?: ManualMeasurement[];
}) {
  const renderGlobalMeasurements = () => (
    <AiMappingGlobalMeasurements
      controller={controller}
      combinedMeasuredAreaSqft={combinedMeasuredAreaSqft}
      manualPerimeterFeet={manualPerimeterFeet}
      manualMeasurements={manualMeasurements}
    />
  );

  const renderBody = () => {
    if (controller.loading && !controller.run) {
      return (
        <MDBox display="flex" flexDirection="column" gap={0.75}>
          <MDBox display="flex" justifyContent="center" p={2} data-testid="ai-mapping-loading">
            <CircularProgress size={28} />
          </MDBox>
          {renderGlobalMeasurements()}
        </MDBox>
      );
    }

    if (controller.loadError && !controller.run) {
      return (
        <MDBox textAlign="center" p={2} data-testid="ai-mapping-load-error">
          <MDTypography variant="body2" color="error">
            {controller.loadError}
          </MDTypography>
          <MDButton
            color="info"
            variant="outlined"
            size="small"
            onClick={controller.retryLoadRun}
            sx={{ mt: 1 }}
            data-testid="ai-mapping-load-retry"
          >
            Try again
          </MDButton>
        </MDBox>
      );
    }

    if (controller.unauthorized) {
      return <AiMappingUpsell />;
    }

    if (controller.drawSession) {
      return (
        <>
          <AiMappingDrawForm
            session={controller.drawSession}
            saving={controller.savingArea}
            error={controller.formError}
            name={controller.defaultAreaName ?? "Untitled Area"}
            onSave={controller.saveArea}
            onCancel={controller.cancelDrawing}
          />
          {renderGlobalMeasurements()}
        </>
      );
    }

    return (
      <MDBox display="flex" flexDirection="column" gap={0.75}>
        <AiMappingRunAreaEditor controller={controller} />
        {renderGlobalMeasurements()}
      </MDBox>
    );
  };

  return (
    <MDBox p={1} data-testid="ai-mapping-sidebar-content">
      {renderBody()}
    </MDBox>
  );
}
